// What the payslip document prints, worked out from a /api/payslip-view
// record. Every field arrives as a string ("" for null); the rows are the
// same arithmetic as payslipCalc.js, read backwards from the stored figures.

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const field = (value) => (value && typeof value === "object" ? value.value : value);

const num = (value) => {
  const n = Number(field(value));
  return Number.isFinite(n) ? n : 0;
};

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const INR = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "₹46,000.00", with Indian digit grouping ("₹1,23,456.00"). */
export const rupees = (value) => {
  const n = round2(num(value));
  return `${n < 0 ? "-" : ""}₹${INR.format(Math.abs(n))}`;
};

const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
  "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const belowHundred = (n) =>
  n < 20 ? ONES[n] : TENS[Math.floor(n / 10)] + (n % 10 ? `-${ONES[n % 10]}` : "");

const belowThousand = (n) => {
  const hundreds = Math.floor(n / 100);
  const rest = n % 100;
  return [hundreds ? `${ONES[hundreds]} Hundred` : "", rest ? belowHundred(rest) : ""]
    .filter(Boolean)
    .join(" ");
};

// Indian system: crore, lakh, thousand, then the last three digits.
const wholeWords = (n) => {
  if (n === 0) return "Zero";
  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rest = n % 1000;
  return [
    crore ? `${wholeWords(crore)} Crore` : "",
    lakh ? `${belowHundred(lakh)} Lakh` : "",
    thousand ? `${belowHundred(thousand)} Thousand` : "",
    rest ? belowThousand(rest) : "",
  ]
    .filter(Boolean)
    .join(" ");
};

/** "Indian Rupee Forty-Six Thousand Only" / "... and Fifty Paise Only". */
export const amountInWords = (value) => {
  const paiseTotal = Math.round(Math.abs(num(value)) * 100);
  const whole = Math.floor(paiseTotal / 100);
  const paise = paiseTotal % 100;
  const words = `Indian Rupee ${wholeWords(whole)}${paise ? ` and ${belowHundred(paise)} Paise` : ""} Only`;
  return num(value) < 0 ? `Minus ${words}` : words;
};

/** "April 2026" */
export const payPeriod = (payslip) => {
  const month = num(payslip?.month);
  return month >= 1 && month <= 12 ? `${MONTH_NAMES[month - 1]} ${payslip?.year ?? ""}`.trim() : "";
};

/** The day the slip was created, as "07/05/2026"; "" when unknown. */
export const payDate = (payslip) => {
  const seconds = num(payslip?.created_at);
  if (!seconds) return "";
  const date = new Date(seconds * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

/**
 * The earnings and deductions columns and their totals.
 *
 * Earnings are the salary bifurcation (basic + allowances, which add up to
 * actual_salary) plus any bonuses. Deductions take that gross down to the
 * credited salary: loss of pay (actual_salary - in_hand_salary), tax % and
 * deduction % of in hand, PF and ESI. Basic, income tax and PF always show;
 * any other row shows only when it is not zero.
 */
export const payslipFigures = (payslip) => {
  const p = payslip || {};
  const inHand = num(p.in_hand_salary);
  const taxPercent = num(p.tax_percentage);
  const deductionPercent = num(p.deduction_percentage);

  const earnings = [
    { label: "Basic", amount: num(p.basic_bifurcation), always: true },
    { label: "House Rent Allowance", amount: num(p.house_rent) },
    { label: "Conveyance Allowance", amount: num(p.conveyance_allowance) },
    { label: "Medical Allowance", amount: num(p.medical_allowance) },
    { label: "Telephone & Internet Allowance", amount: num(p.internet_phone_allowance) },
    { label: "Other Allowance", amount: num(p.other_bifurcation) },
    { label: "Extra Working Bonus", amount: num(p.extra_working_bonus) },
    { label: "Bonus", amount: num(p.random_bonus) },
  ];

  const lossOfPay = p.in_hand_salary === "" || p.in_hand_salary == null ? 0 : num(p.actual_salary) - inHand;
  const deductions = [
    { label: "Income Tax", amount: round2((inHand * taxPercent) / 100), always: true },
    { label: "Provident Fund", amount: num(p.pf), always: true },
    { label: "Employee State Insurance", amount: num(p.emp_state_insurance) },
    { label: "Loss of Pay", amount: round2(lossOfPay) },
    { label: `Deduction (${deductionPercent}%)`, amount: round2((inHand * deductionPercent) / 100) },
  ];

  const shown = (rows) =>
    rows.filter((row) => row.always || Math.abs(row.amount) >= 0.005).map(({ label, amount }) => ({ label, amount }));
  const total = (rows) => round2(rows.reduce((sum, row) => sum + row.amount, 0));

  const earningRows = shown(earnings);
  const deductionRows = shown(deductions);
  const gross = total(earningRows);
  const totalDeductions = total(deductionRows);
  const credited = Number(field(p.credited_salary));

  const workDays = num(p.total_work_days);
  const lopDays = num(p.paid_leaves);

  return {
    earnings: earningRows,
    deductions: deductionRows,
    gross,
    totalDeductions,
    // the stored credited salary is what was paid; fall back to the sum
    net: p.credited_salary !== "" && Number.isFinite(credited) ? credited : round2(gross - totalDeductions),
    paidDays: round2(workDays - lopDays),
    lopDays,
  };
};
